export const termsSections = [
  {
    id: 'agreement-to-terms',
    title: 'Agreement to Terms',
    content: (
      <>
        <p>
          By accessing or using the SmallSmall platform (“Web and app”), you
          agree to be bound by these <strong>Terms of Use</strong>. Please read
          the article carefully. This agreement constitutes a legally binding
          contract between you (the “User”) and SmallSmall Technology Ltd.
          ("we", "us", or "our").
        </p>
        <p>
          If you do not agree with these terms, please do not use the web or
          app.
        </p>
      </>
    ),
  },
  {
    id: 'who-we-are-and-what-we-do',
    title: 'Who we are and what we do',
    content: (
      <>
        <p>
          The SmallSmall platform (web and app) is an online marketplace for the
          sale of goods and services offering:
        </p>
        <ul className="list-disc ml-6">
          <li>An online marketplace to buy and sell items, rent a home</li>
          <li>Access to exclusive product deals from partner vendors</li>
          <li>Flexible installment payment options</li>
          <li>Safe and secure transaction processing</li>
        </ul>
        <p>
          We aim to deliver a reliable, accessible, and user-friendly
          experience.
        </p>
      </>
    ),
  },
  {
    id: 'usage-guidelines',
    title: 'Usage Guidelines',
    content: (
      <>
        <p>To maintain platform integrity, users must:</p>
        <ul className="list-disc ml-6">
          <li>Use strong, unique passwords</li>
          <li>Keep credentials private and secure</li>
          <li>Regularly update passwords</li>
          <li>Report unauthorized access immediately</li>
        </ul>
        <p>
          Be vigilant against phishing—SmallSmall will never request sensitive
          details via email.
        </p>
      </>
    ),
  },
  {
    id: 'user-conduct-policy',
    title: 'User Conduct Policy',
    content: (
      <>
        <p>
          <strong>A. Behaviour Expectations</strong>
        </p>
        <p>
          Users must act respectfully, professionally, and lawfully on the
          platform. Hate speech, discriminatory behavior, and harassment are
          strictly prohibited.
        </p>
        <p>
          <strong>B. Prohibited Conduct</strong>
        </p>
        <ul className="list-disc ml-6">
          <li>Harassment, impersonation, or abusive behavior</li>
          <li>Fraudulent listings or transactions</li>
          <li>Distribution of spam or phishing links</li>
          <li>Sale of illegal, counterfeit, or stolen items</li>
          <li>Violation of Nigerian laws or app policies</li>
        </ul>
        <p>
          <strong>C. Consequences</strong>
        </p>
        <p>
          Violation may result in temporary suspension or permanent account
          termination, without notice.
        </p>
      </>
    ),
  },
  {
    id: 'privacy-and-data-protection',
    title: 'Privacy and Data Protection',
    content: (
      <>
        <p>We prioritize your privacy. Data collected is used to:</p>
        <ul className="list-disc ml-6">
          <li>Improve app functionality</li>
          <li>Personalize user experience</li>
          <li>Ensure account security</li>
        </ul>
        <p>
          Your data is never sold and will only be shared as required by law.
        </p>
      </>
    ),
  },
  {
    id: 'pricing-policy',
    title: 'Pricing Policy',
    content: (
      <>
        <ul className="list-disc ml-6">
          <li>All prices shown are final at the time of order placement.</li>
          <li>
            Vendors must honor listed prices and may not amend pricing after
            purchase confirmation.
          </li>
          <li>
            The platform is not responsible for price inaccuracies caused by
            vendor error, but complaints will be investigated promptly.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: 'down-payment-policy',
    title: 'Down Payment Policy',
    content: (
      <>
        <p>
          <strong>a. Maximum Down Payment Limit</strong>
        </p>
        <ul className="list-disc ml-6">
          <li>Buyers may pay a maximum 60% down payment at checkout.</li>
        </ul>

        <p>
          <strong>b. Flexible Options</strong>
        </p>
        <ul className="list-disc ml-6">
          <li>
            Vendors may choose to accept lower percentages, subject to product
            listing or promotional terms.
          </li>
        </ul>

        <p>
          <strong>c. Remaining Balance</strong>
        </p>
        <ul className="list-disc ml-6">
          <li>
            The remaining amount is payable via the listed installment plan.
          </li>
        </ul>

        <p>
          <strong>d. Enforcement</strong>
        </p>
        <ul className="list-disc ml-6">
          <li>
            Vendors may not collect more than 60% full. Violators risk account
            penalties.
          </li>
        </ul>

        <p>
          <strong>e. Acknowledgment</strong>
        </p>
        <ul className="list-disc ml-6">
          <li>
            By placing a down payment, both buyers and vendors agree to comply
            with this policy.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: 'general-payment-terms',
    title: 'General payment terms (Buyer)',
    content: (
      <>
        <p>Buyers may complete transactions through:</p>
        <ul className="list-disc ml-6">
          <li>full Payments using debit/credit card, or bank transfer.</li>
          <li>
            Installment Plans, which include:
            <ul className="list-disc ml-6">
              <li>Daily, Weekly, or Monthly deductions</li>
              <li>Payments automated via Direct Debit Mandate</li>
              <li>Buyers must authorize a debit setup at checkout</li>
            </ul>
          </li>
        </ul>
        <p>
          Installments are scheduled as agreed upon at the time of order and
          subject to system reminders and follow-ups.
        </p>
      </>
    ),
  },
  {
    id: 'payment-processing',
    title: 'Payment Processing',
    content: (
      <>
        <p>
          For all full and direct debit payments, SmallSmall partners with
          Paystack and Mono. By making purchases on our platform, you authorize
          us to process payments through these secure third-party providers. You
          may also be required to consent to the terms and privacy policies of
          Paystack or Mono, as applicable.
        </p>
      </>
    ),
  },
  {
    id: 'placing-an-order',
    title: 'Placing an Order (Buyer)',
    content: (
      <>
        <p>To order:</p>
        <ol className="list-decimal ml-6">
          <li>Select your desired product.</li>
          <li>Choose a buying plan (full, monthly, weekly, or daily).</li>
          <li>Add to cart and proceed to checkout.</li>
          <li>Input delivery details and confirm order.</li>
          <li>
            Receive email and in-app confirmation (for installment plans, setup
            direct debit mandate).
          </li>
        </ol>
        <p className="mt-2">
          <strong>Note:</strong> Orders are processed only after confirmation of
          full successful payment or first installment downpayment.
        </p>
      </>
    ),
  },
  {
    id: 'delivery-terms',
    title: 'Delivery Terms (Buyer)',
    content: (
      <>
        <ul className="list-disc ml-6">
          <li>Estimated delivery dates are provided at checkout.</li>
          <li>
            Orders are fulfilled by SmallSmall, vendors, or third-party
            couriers.
          </li>
          <li>
            Buyers are responsible for providing accurate delivery details.
          </li>
          <li>
            Failed deliveries due to inaccurate info may attract extra charges.
          </li>
          <li>
            Buyers should inspect items upon delivery and report issues within
            24 hours.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: 'change-of-mind',
    title: 'Change of Mind After Purchase (Buyer)',
    content: (
      <>
        <ul className="list-disc ml-6">
          <li>
            Orders may be canceled before dispatch by contacting support:{' '}
            <code>support@smallsmall.com</code>
          </li>
          <li>
            Once dispatched or delivered, orders are non-refundable unless the
            item is:
            <ul className="list-disc ml-6">
              <li>Defective</li>
              <li>Incorrect</li>
              <li>Covered by vendor’s return policy</li>
            </ul>
          </li>
          <li>
            Change of mind is not a valid return reason unless expressly stated.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: 'account-closure',
    title: 'Account Closure (Buyer and Vendor)',
    content: (
      <>
        <p>Users may close accounts by:</p>
        <ul className="list-disc ml-6">
          <li>Visiting “Account Settings” in the app</li>
          <li>Ensuring all pending transactions are completed</li>
          <li>Requesting deletion via support email for web</li>
        </ul>
        <p>After closure:</p>
        <ul className="list-disc ml-6">
          <li>Data is archived for legal and audit purposes</li>
          <li>
            Users will no longer have access to their purchase or sales history
          </li>
        </ul>
      </>
    ),
  },
  {
    id: 'warranties-on-purchased-items',
    title: 'Warranties on Purchased Items (Buyer)',
    content: (
      <>
        <p>
          Warranties vary by product and vendor. Buyers are responsible for:
        </p>
        <ul className="list-disc ml-6">
          <li>Reviewing warranty details listed on the product page</li>
          <li>Requesting warranty support directly from vendors</li>
        </ul>
        <p>
          The platform does not offer or enforce product warranties unless
          specifically stated.
        </p>
      </>
    ),
  },
  {
    id: 'liability-for-products-and-services',
    title: 'Liability for Products and Services (Buyer)',
    content: (
      <>
        <p>
          SmallSmall operates as a marketplace and is also a main seller of
          items.
        </p>
        <ul className="list-disc ml-6">
          <li>
            Vendors are responsible for product quality, authenticity, and
            performance.
          </li>
          <li>
            The platform disclaims liability for:
            <ul className="list-disc ml-6">
              <li>Product defects</li>
              <li>Misleading product information</li>
            </ul>
          </li>
          <li>
            Buyers are encouraged to report vendor or any misconduct
            immediately.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: 'indemnification-by-buyers',
    title: 'Indemnification by Buyers',
    content: (
      <>
        <p>
          You agree to indemnify and hold harmless SmallSmall, its officers,
          employees, and affiliates from claims or liabilities arising from:
        </p>
        <ul className="list-disc ml-6">
          <li>Breach of these Terms</li>
          <li>Misuse of the web/app</li>
          <li>Fraudulent activity</li>
          <li>Violation of laws or third-party rights</li>
        </ul>
      </>
    ),
  },
  {
    id: 'modification-of-terms',
    title: 'Modification of Terms',
    content: (
      <>
        <p>
          We may update these Terms periodically. Users will be notified of
          changes via:
        </p>
        <ul className="list-disc ml-6">
          <li>Email</li>
          <li>In-app notifications</li>
        </ul>
        <p>
          Continued use after such updates constitutes acceptance of the revised
          Terms.
        </p>
      </>
    ),
  },
  {
    id: 'identity-verification-and-debt-profile-check',
    title: 'Identity Verification and Debt Profile Check',
    content: (
      <>
        <p>
          To avoid fraud or payment default and protect our community of vendors
          and buyers, SmallSmall requires all buyers to verify their identity
          and debt profile before or at the point of placing an order.Users can
          access the account verification steps via the account profile tab on
          the account settings. For identity verification a pop up will appear
          on the screen requesting the user to complete the ID verification
          process. This may include submission of government-issued ID and/or
          biometric or phone number validation, as applicable.
        </p>
        <ul className="list-disc ml-6">
          <li>
            For debt profile verification users must enter and submit their BVN
            for our third party partner platforms to successfully check the debt
            profile of a user.
          </li>
          <li>
            By verifying your identity and debt profile, you consent to
            SmallSmall conducting a background assessment that includes:
          </li>
          <ul className="list-disc ml-6">
            <li>
              Checking your debt profile through licensed credit bureaus or
              third-party debt registries
            </li>
            <li>
              Verifying whether there are any ongoing or pending loans or unpaid
              obligations associated with your identity
            </li>
          </ul>
          <li>
            This process is mandatory for all buyers using instalment payment
            options, and may also be required for certain high-value
            transactions paid full.
          </li>
          <li>
            The information collected will be used solely for risk assessment
            and fraud prevention in accordance with our Privacy Policy.
          </li>
          <li>
            Failure to complete identity and debt profile verification or a
            failed debt profile may result in:
          </li>
          <ul className="list-disc ml-6">
            <li>Account suspension</li>
            <li>Inability to proceed with purchase</li>
            <li>Reporting to monitoring agencies</li>
          </ul>
        </ul>
      </>
    ),
  },
  {
    id: 'item-returns-and-defective-products',
    title: 'Item Returns and Defective Products',
    content: (
      <>
        <p>
          We are committed to ensuring customer satisfaction. If a purchased
          item is defective, the following return policy applies:
        </p>
        <ul className="list-disc ml-6">
          <li>
            <strong>Return Notification:</strong> Buyers must notify SmallSmall
            within 24 hours of receiving the item if a defect is detected. Buyer
            should document defect by making a video or taking several pictures
            and share with support team of SmallSmall. Failure to raise a defect
            issue within this timeframe means the buyer assumes responsibility
            for the item, and a return may not be accepted.
          </li>
          <li>
            <strong>Defective Delivery:</strong> If the item was already
            defective before reaching the delivery company, the vendor shall
            bear the full responsibility and cost of return or replacement.
          </li>
          <ul className="list-disc ml-6">
            <li>
              If the item was damaged during the delivery process, the delivery
              company shall be liable for the defect and associated loss.
            </li>
            <li>
              If a defect is reported after 24 hours from the time of delivery,
              the buyer bears full responsibility, and no refund or replacement
              will be issued.
            </li>
          </ul>
          <li>
            <strong>Post-Delivery Damage:</strong> Buyer bears cost if defect is
            reported after 24 hours.
          </li>
          <li>
            <strong>Return Process:</strong> All returns must be packaged with
            original delivery package and label and sent back through an agreed
            delivery method. The buyer is responsible for the cost of return
            delivery, unless otherwise approved due to vendor or delivery
            liability.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: 'vendor-warranty-obligation',
    title: 'Vendor Warranty Obligation',
    content: (
      <>
        <p>
          To ensure quality and accountability, all vendors listing products on
          SmallSmall must provide a minimum one (1) year warranty on their
          products — particularly for phones, electronics, and other high-value
          items. Vendors failing to meet this requirement will not be eligible
          to list on the platform.
        </p>
      </>
    ),
  },
  {
    id: 'late-payments-and-asset-recovery',
    title: 'Late Payments and Asset Recovery',
    content: (
      <>
        <p>
          Due to the unique structure of our payment package, which allows
          buyers to make installment payments for purchased items, strict
          compliance with payment schedules is required.
        </p>
        <ul className="list-disc ml-6">
          <li>
            If a buyer is 7 days late on their scheduled payment, SmallSmall
            reserves the right to initiate asset recovery procedures.
          </li>
          <li>
            Our collections team will retrieve the item(s) from the customer’s
            possession.
          </li>
          <li>
            Continued default may result in permanent blacklisting from the
            platform, in addition to legal enforcement where necessary.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: 'loyalty-and-discount-coupons',
    title: 'Loyalty and Discount Coupons',
    content: (
      <>
        <p>
          SmallSmall values customer loyalty. Loyal users who consistently meet
          their payment obligations and adhere to platform rules may be eligible
          to win discount coupons.
        </p>
        <ul className="list-disc ml-6">
          <li>
            These coupons can be applied at checkout for a price discount on
            future purchases.
          </li>
          <li>
            Eligibility and reward frequency are determined at SmallSmall's
            discretion and communicated through email or user dashboards.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: 'dispute-resolution',
    title: 'Dispute Resolution',
    content: (
      <>
        <p>
          We strive to resolve all disputes amicably, fairly, and efficiently.
          If you have a concern or dispute with any aspect of your use of the
          platform, the following procedure will apply:
        </p>
        <ul className="list-disc ml-6">
          <li>Initial Complaint</li>
          <ul className="list-disc ml-6">
            <li>
              All complaints must be submitted in writing to
              support@smallsmall.com with full details of the issue.
            </li>
            <li>
              We will acknowledge receipt of your complaint within 48 hours and
              respond substantively within 7 business days.
            </li>
          </ul>
          <li>Vendor or Delivery Disputes</li>
          <ul className="list-disc ml-6">
            <li>
              Where a dispute involves a vendor or delivery partner, we will
              mediate communication and facilitate a resolution in good faith.
            </li>
            <li>
              Our role is neutral, and all evidence (photos, videos, delivery
              notes, etc.) will be taken into account.
            </li>
          </ul>
          <li>Escalation</li>
          <ul className="list-disc ml-6">
            <li>
              If a resolution is not reached at the initial stage, the matter
              may be escalated to our Customer Resolution Team, who will make a
              final internal determination.
            </li>
          </ul>
          <li>External Mediation</li>
          <li>Legal Jurisdiction</li>
          <li>
            Any unresolved dispute shall be subject to the exclusive
            jurisdiction of the courts of Lagos State, Nigeria.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: 'contact-and-feedback',
    title: 'Contact and Feedback',
    content: (
      <>
        <p>We welcome your inquiries and feedback. Reach out at:</p>
        <p>
          <strong>Email:</strong> <code>support@smallsmall.com</code>
        </p>
        <p>
          <strong>Subject:</strong> SmallSmall platform – Terms or Privacy
          Inquiry
        </p>
      </>
    ),
  },
];
